import React, { useState, useEffect } from 'react';
import FormulaireClientModal from '../FormulaireClientModal';
import DashboardHeader from './components/DashboardHeader';
import StatsCards from './components/StatsCards';
import SearchFilters from './components/SearchFilters';
import TabsNavigation from './components/TabsNavigation';
import DocumentsTable from './components/DocumentsTable';
import TransfertsTable from './components/TransfertsTable';
import UsersTable from './components/UsersTable';
import DossierModal from './components/Modals/DossierModal';
import SousDossierModal from './components/Modals/SousDossierModal';
import TransfertModal from './components/Modals/TransfertModal';
import UserModal from './components/Modals/UserModal';
import CreateUserModal from './components/Modals/CreateUserModal';
import RejectModal from './components/Modals/RejectModal';
import DeleteModal from './components/Modals/DeleteModal';
import { 
  DossierClient, 
  DossierTransfert, 
  User, 
  UserForm, 
  ActiveTab, 
  DeleteTarget 
} from './components/utils/types';

const AdminDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<ActiveTab>('documents');
  const [dossiers, setDossiers] = useState<DossierClient[]>([]);
  const [filteredDossiers, setFilteredDossiers] = useState<DossierClient[]>([]);
  const [transferts, setTransferts] = useState<DossierTransfert[]>([]);
  const [filteredTransferts, setFilteredTransferts] = useState<DossierTransfert[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateSearch, setDateSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [currentUserRole, setCurrentUserRole] = useState<string>('superviseur');
  const [currentUser, setCurrentUser] = useState('Superviseur');

  const [showDossierModal, setShowDossierModal] = useState(false);
  const [showSousDossierModal, setShowSousDossierModal] = useState(false);
  const [showTransfertModal, setShowTransfertModal] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showFormulaireModal, setShowFormulaireModal] = useState(false);

  const [selectedDossier, setSelectedDossier] = useState<DossierClient | null>(null);
  const [selectedTransfert, setSelectedTransfert] = useState<DossierTransfert | null>(null);
  const [selectedSousDossier, setSelectedSousDossier] = useState<any>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [dossierToReject, setDossierToReject] = useState<DossierClient | null>(null);
  const [transfertToReject, setTransfertToReject] = useState<DossierTransfert | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const [userForm, setUserForm] = useState<UserForm>({
    username: '',
    email: '',
    password: '',
    role: 'superviseur'
  });

  const API_URL = process.env.REACT_APP_API_URL || 'http://31.220.82.147:5000/';

  const handleAuthError = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('dossiers_statuts');
    window.location.href = '/admin/login';
  };

const formatDate = (date: any): string => {
    if (!date) return 'N/A';
    try {
      return new Date(date).toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    } catch {
      return 'N/A';
    }
  };

  const canDeleteSubfoldersAndFiles = () => {
    return currentUserRole === 'superviseur' || 
           currentUserRole === 'conformité' || 
           currentUserRole === 'admin_bank';
  };

  const loadAllUsersForExport = async (): Promise<User[]> => {
    try {
      const response = await fetch(`${API_URL}/api/users`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      const data = await response.json();
      
      if (!response.ok || !data.success) {
        throw new Error('Erreur lors du chargement des utilisateurs');
      }

      return data.users;
    } catch (error) {
      console.error('Erreur chargement utilisateurs pour export:', error);
      return [];
    }
  };

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        if (user.username) setCurrentUser(user.username);
        if (user.role) setCurrentUserRole(user.role);
      } catch (error) {
        console.error('Erreur lors du chargement de l\'utilisateur:', error);
      }
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'documents' || activeTab === 'transferts') {
      loadDossiers();
    } else {
      loadUsers();
    }
  }, [activeTab, currentUserRole]);

  useEffect(() => {
    if (currentUserRole === 'super_admin') {
      const switchToUsers = async () => {
        await loadUsers();
        setActiveTab('users');
      };
      switchToUsers();
    }
  }, [currentUserRole]);

  const loadDossiers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const url = `${API_URL}/api/documents/admin/dossiers`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        if (response.status === 401) {
          handleAuthError();
          return;
        }
        const errorText = await response.text();
        console.error('Erreur HTTP:', response.status, errorText);
        alert('Une erreur est survenue lors du chargement des données');
        return;
      }

      const data = await response.json();
      
      if (data.success) {
        const allDocuments = data.dossiers;
        const documentsVoyage = allDocuments.filter((d: DossierClient) => 
          d.typeDocument === 'voyage' || !d.typeDocument
        );
        const documentsTransfert = allDocuments.filter((d: DossierClient) => 
          d.typeDocument === 'transfert'
        );

        let filteredVoyage = documentsVoyage;
        let filteredTransfert = documentsTransfert;

        if (currentUserRole === 'agent_saisie') {
          filteredVoyage = filteredVoyage.filter((d: DossierClient) => 
            d.statut !== 'archivé' && d.statut !== 'rejeté'
          );
          filteredTransfert = filteredTransfert.filter((d: DossierClient) => 
            d.statut !== 'archivé' && d.statut !== 'rejeté'
          );
        } else if (currentUserRole !== 'conformité') {
          filteredVoyage = filteredVoyage.filter((d: DossierClient) => 
            d.statut !== 'rejeté'
          );
          filteredTransfert = filteredTransfert.filter((d: DossierClient) => 
            d.statut !== 'rejeté'
          );
        }

        setDossiers(filteredVoyage);
        setFilteredDossiers(filteredVoyage);

        const transfertsFormates: DossierTransfert[] = filteredTransfert.map((d: DossierClient) => {
          const premierSousDossier = d.sousDossiers[0];
          const entrepriseValue = premierSousDossier?.entreprise || d.entreprise || 'Pas défini';

          return {
            reference: d.reference,
            nom: d.nom,
            prenom: d.prenom,
            email: d.email,
            telephone: d.telephone,
            profession: d.profession,
            sexe: d.sexe,
            typeTransfert: d.typeTransfert || '',
            dateDebut: d.dateDebut,
            dateFin: d.dateFin,
            fichiers: premierSousDossier?.fichiers || [],
            statut: d.statut,
            dateCreation: d.dateCreation,
            motifRejet: d.motifRejet,
            entreprise: entrepriseValue
          };
        });

        setTransferts(transfertsFormates);
        setFilteredTransferts(transfertsFormates);
      }
    } catch (error) {
      console.error('Erreur chargement:', error);
      if (error instanceof Error) {
        alert(`Erreur: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/users`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        if (response.status === 401) {
          handleAuthError();
          return;
        }
        alert('Une erreur est survenue lors du chargement des utilisateurs');
        return;
      }

      if (data.success) {
        let filteredUsers = data.users;
        
        if (currentUserRole === 'super_admin') {
          filteredUsers = data.users.filter((u: User) => 
            u.role === 'admin_bank' || u.role === 'super_admin'
          );
        } else if (currentUserRole === 'admin_bank') {
          filteredUsers = data.users.filter((u: User) => 
            u.role !== 'super_admin' && u.role !== 'admin_bank'
          );
        }

        setUsers(filteredUsers);
        setFilteredUsers(filteredUsers);
      }
    } catch (error) {
      console.error('Erreur chargement utilisateurs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    if (activeTab === 'documents') {
      let filtered = dossiers;

      if (searchTerm.trim()) {
        filtered = filtered.filter(dossier =>
          dossier.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
          dossier.prenom.toLowerCase().includes(searchTerm.toLowerCase()) ||
          dossier.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          dossier.reference.toLowerCase().includes(searchTerm.toLowerCase()) ||
          dossier.profession?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (Array.isArray(dossier.pays) 
            ? dossier.pays.some(p => p.toLowerCase().includes(searchTerm.toLowerCase()))
            : dossier.pays?.toLowerCase().includes(searchTerm.toLowerCase())) ||
          dossier.typeTransfert?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          dossier.motif?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          dossier.telephone?.includes(searchTerm)
        );
      }

      if (dateSearch.trim()) {
        const searchDate = new Date(dateSearch).toLocaleDateString('fr-FR');
        filtered = filtered.filter(dossier => {
          const dossierDate = new Date(dossier.dateCreation).toLocaleDateString('fr-FR');
          return dossierDate === searchDate;
        });
      }

      setFilteredDossiers(filtered);
    } else if (activeTab === 'transferts') {
      let filtered = transferts;

      if (searchTerm.trim()) {
        filtered = filtered.filter(transfert =>
          transfert.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
          transfert.prenom.toLowerCase().includes(searchTerm.toLowerCase()) ||
          transfert.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          transfert.entreprise?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          transfert.reference.toLowerCase().includes(searchTerm.toLowerCase()) ||
          transfert.profession?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          transfert.typeTransfert.toLowerCase().includes(searchTerm.toLowerCase()) ||
          transfert.telephone?.includes(searchTerm)
        );
      }

      if (dateSearch.trim()) {
        const searchDate = new Date(dateSearch).toLocaleDateString('fr-FR');
        filtered = filtered.filter(transfert => {
          const transfertDate = new Date(transfert.dateCreation).toLocaleDateString('fr-FR');
          return transfertDate === searchDate;
        });
      }

      setFilteredTransferts(filtered);
    } else {
      const filtered = users.filter(user =>
        user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredUsers(filtered);
    }
  };

  const resetSearch = () => {
    setSearchTerm('');
    setDateSearch('');
    if (activeTab === 'documents') {
      setFilteredDossiers(dossiers);
    } else if (activeTab === 'transferts') {
      setFilteredTransferts(transferts);
    } else {
      setFilteredUsers(users);
    }
  };

  const changeStatut = async (reference: string, newStatut: any) => {
    if (newStatut === 'rejeté' && (currentUserRole === 'conformité' || currentUserRole === 'superviseur')) {
      const dossier = dossiers.find(d => d.reference === reference);
      setDossierToReject(dossier || null);
      setShowRejectModal(true);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/documents/admin/statut/${reference}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ statut: newStatut })
      });

      if (response.ok) {
        setDossiers(prev => prev.map(d =>
          d.reference === reference ? { ...d, statut: newStatut } : d
        ));
        setFilteredDossiers(prev => prev.map(d =>
          d.reference === reference ? { ...d, statut: newStatut } : d
        ));
      }
    } catch (error) {
      console.error('Erreur changement statut:', error);
    }
  };

  const changeTransfertStatut = async (reference: string, newStatut: any) => {
    if (newStatut === 'rejeté' && (currentUserRole === 'conformité' || currentUserRole === 'superviseur')) {
      const transfert = transferts.find(t => t.reference === reference);
      setTransfertToReject(transfert || null);
      setShowRejectModal(true);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/documents/admin/statut/${reference}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ statut: newStatut })
      });

      if (response.ok) {
        setTransferts(prev => prev.map(t =>
          t.reference === reference ? { ...t, statut: newStatut } : t
        ));
        setFilteredTransferts(prev => prev.map(t =>
          t.reference === reference ? { ...t, statut: newStatut } : t
        ));
      }
    } catch (error) {
      console.error('Erreur changement statut transfert:', error);
    }
  };

  const confirmReject = async () => {
    const target = dossierToReject || transfertToReject;
    if (!target || !rejectReason.trim()) {
      alert('Veuillez saisir un motif de rejet');
      return;
    }

    const endpoint = `${API_URL}/api/documents/admin/statut/${target.reference}`;

    try {
      const response = await fetch(endpoint, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          statut: 'rejeté',
          motifRejet: rejectReason
        })
      });

      if (response.ok) {
        if (dossierToReject) {
          setDossiers(prev => prev.map(d =>
            d.reference === dossierToReject.reference ? { ...d, statut: 'rejeté' } : d
          ));
          setFilteredDossiers(prev => prev.map(d =>
            d.reference === dossierToReject.reference ? { ...d, statut: 'rejeté' } : d
          ));
        } else if (transfertToReject) {
          setTransferts(prev => prev.map(t =>
            t.reference === transfertToReject.reference ? { ...t, statut: 'rejeté' } : t
          ));
          setFilteredTransferts(prev => prev.map(t =>
            t.reference === transfertToReject.reference ? { ...t, statut: 'rejeté' } : t
          ));
          loadDossiers();
        }

        setShowRejectModal(false);
        setRejectReason('');
        setDossierToReject(null);
        setTransfertToReject(null);
        alert('Dossier rejeté avec succès');
      }
    } catch (error) {
      console.error('Erreur rejet:', error);
    }
  };

//  const exportToExcel = async () => {
    // Implémentation de l'export Excel
  //};


const exportToExcel = async () => {
  if (currentUserRole !== 'auditeur' && currentUserRole !== 'conformité') {
    alert('Seuls les auditeurs et conformité peuvent exporter');
    return;
  }

  let dataToExport: any[] = [];
  let filename = '';
  let sheetName = '';

  if (activeTab === 'documents') {
    dataToExport = filteredDossiers.map(dossier => ({
      'Référence': dossier.reference,
      'Nom': dossier.nom,
      'Prénom': dossier.prenom,
      'Email': dossier.email,
      'Téléphone': dossier.telephone,
      'Profession': dossier.profession,
      'Sexe': dossier.sexe,
      'Type': dossier.typeDocument,
      //'Pays': Array.isArray(dossier.pays) ? dossier.pays.join(', ') : dossier.pays,
      //'Motif': dossier.motif,
      'Statut': dossier.statut,
      'Date de création': formatDate(dossier.dateCreation),
      //'Date début': formatDate(dossier.dateDebut),
      //'Date fin': formatDate(dossier.dateFin),
      'Nombre de sous-dossiers': dossier.sousDossiers.length
    }));
    filename = `documents_voyages_${new Date().toISOString().split('T')[0]}.xlsx`;
    sheetName = 'Documents de Voyage';
  } else if (activeTab === 'transferts') {
    dataToExport = filteredTransferts.map(transfert => ({
      'Référence': transfert.reference,
      'Nom': transfert.nom,
      'Prénom': transfert.prenom,
      'Email': transfert.email,
      'Téléphone': transfert.telephone,
      'Profession': transfert.profession,
      'Sexe': transfert.sexe,
      'Entreprise': transfert.entreprise || 'Pas défini',
      'Type Transfert': transfert.typeTransfert,
      'Statut': transfert.statut,
      'Date de création': formatDate(transfert.dateCreation),
      'Nombre de fichiers': transfert.fichiers.length
    }));
    filename = `dossiers_transferts_${new Date().toISOString().split('T')[0]}.xlsx`;
    sheetName = 'Dossiers de Transferts';
  }

  // Vérifier s'il y a des données à exporter
  if (dataToExport.length === 0) {
    alert('Aucune donnée à exporter');
    return;
  }

  try {
    // Importer la bibliothèque XLSX dynamiquement
    const XLSX = await import('xlsx');

    // Créer un nouveau classeur
    const workbook = XLSX.utils.book_new();

    // Convertir les données en feuille de calcul
    const worksheet = XLSX.utils.json_to_sheet(dataToExport);

    // Définir les largeurs de colonnes automatiquement
    const colWidths = Object.keys(dataToExport[0]).map(key => ({
      wch: Math.max(
        key.length, // Largeur du header
        ...dataToExport.map(row =>
          String(row[key] || '').length // Largeur du contenu le plus long
        )
      )
    }));

    worksheet['!cols'] = colWidths;
   // Ajouter des styles de base (en-têtes en gras)
    const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const headerCell = XLSX.utils.encode_cell({ r: range.s.r, c: C });
      if (worksheet[headerCell]) {
        worksheet[headerCell].s = {
          font: { bold: true },
          fill: { fgColor: { rgb: "D3D3D3" } } // Fond gris pour les en-têtes
        };
      }
    }

    // Ajouter la feuille au classeur
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

    // Générer le fichier et le télécharger
    XLSX.writeFile(workbook, filename);

  } catch (error) {
    console.error('Erreur lors de l\'export Excel:', error);
    alert('Erreur lors de l\'exportation vers Excel');
  }
};





//  const exportUsersToExcel = async () => {
    // Implémentation de l'export utilisateurs
  //};

const exportUsersToExcel = async () => {
  if (currentUserRole !== 'super_admin') {
    alert('Seuls les super administrateurs peuvent exporter la liste des utilisateurs');
    return;
  }

  try {
    // Charger TOUS les utilisateurs spécifiquement pour l'export
    const allUsers = await loadAllUsersForExport();

    if (allUsers.length === 0) {
      alert('Aucun utilisateur à exporter');
      return;
    }

    let dataToExport: any[] = [];
    let filename = '';
    let sheetName = '';

    // Préparer les données de TOUS les utilisateurs
    dataToExport = allUsers.map(user => ({
      'Nom d\'utilisateur': user.username,
      'Email': user.email,
      'Rôle': user.role === 'super_admin' ? 'Super Admin' :
              user.role === 'admin_bank' ? 'Administrateur Banque' :
              user.role === 'agent_saisie' ? 'Agent de Saisie' :
              user.role === 'auditeur' ? 'Auditeur' :
              user.role === 'conformité' ? 'Conformité' : 'Superviseur',
      'Statut': user.isActive ? 'Actif' : 'Inactif',
      'Date de création': formatDate(user.createdAt)
    }));

    filename = `liste_utilisateurs_${new Date().toISOString().split('T')[0]}.xlsx`;
    sheetName = 'Liste des utilisateurs';

    // Importer la bibliothèque XLSX dynamiquement
    const XLSX = await import('xlsx');

    // Créer un nouveau classeur
    const workbook = XLSX.utils.book_new();

    // Convertir les données en feuille de calcul
    const worksheet = XLSX.utils.json_to_sheet(dataToExport);

    // Définir les largeurs de colonnes automatiquement
    const colWidths = Object.keys(dataToExport[0]).map(key => ({
      wch: Math.max(
        key.length, // Largeur du header
        ...dataToExport.map(row =>
          String(row[key] || '').length // Largeur du contenu le plus long
        )
      )
    }));

    worksheet['!cols'] = colWidths;

    // Ajouter des styles de base (en-têtes en gras)
    const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const headerCell = XLSX.utils.encode_cell({ r: range.s.r, c: C });
      if (worksheet[headerCell]) {
        worksheet[headerCell].s = {
          font: { bold: true },
          fill: { fgColor: { rgb: "D3D3D3" } } // Fond gris pour les en-têtes
        };
      }
    }

    // Ajouter la feuille au classeur
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

    // Générer le fichier et le télécharger
    XLSX.writeFile(workbook, filename);

  } catch (error) {
    console.error('Erreur lors de l\'export Excel des utilisateurs:', error);
    alert('Erreur lors de l\'exportation vers Excel');
  }
};


  //const createUser = async () => {
    // Implémentation création utilisateur
 // };

  const createUser = async () => {
  try {
    const response = await fetch(`${API_URL}/api/users`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify(userForm)
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || `Erreur ${response.status}`);
    }

    await loadUsers();
    setShowCreateUserModal(false);
    setUserForm({
      username: '',
      email: '',
      password: '',
      role: currentUserRole === 'super_admin' ? 'admin_bank' : 'superviseur'
    });
    alert('Utilisateur créé avec succès');

  } catch (error) {
    console.error('Erreur création utilisateur:', error);

    // Correction TypeScript
    if (error instanceof Error) {
      alert(error.message);
    } else {
      alert('Erreur inconnue lors de la création');
    }
  }
};




//  const updateUser = async () => {
    // Implémentation mise à jour utilisateur
  //};
//
  const updateUser = async () => {
  if (!selectedUser) return;

  try {
    const updateData: any = {
      username: userForm.username,
      email: userForm.email,
      role: userForm.role
    };

    if (userForm.password) {
      updateData.password = userForm.password;
    }

    const response = await fetch(`${API_URL}/api/users/${selectedUser._id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify(updateData)
    });

    const data = await response.json();

    if (response.ok) {
      await loadUsers();
      setShowUserModal(false);
      alert('Utilisateur modifié avec succès');
    } else {
      throw new Error(data.message || 'Modification échouée');
    }
  } catch (error) {
    console.error('Erreur modification utilisateur:', error);

    // Correction TypeScript
    if (error instanceof Error) {
      alert(error.message);
    } else {
      alert('Erreur inconnue lors de la modification');
    }
  }
};



//  const toggleUserStatus = async (userId: string) => {
    // Implémentation changement statut utilisateur
  //};

    const toggleUserStatus = async (userId: string) => {
    if (currentUserRole !== 'admin_bank' && currentUserRole !== 'super_admin') {
      alert('Seuls les administrateurs peuvent modifier les utilisateurs');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          isActive: !users.find(u => u._id === userId)?.isActive
        })
      });

      if (response.ok) {
        await loadUsers();
      }
    } catch (error) {
      console.error('Erreur lors du changement de statut utilisateur:', error);
    }
  };


  //const downloadFile = async (fileId: string, nomOriginal: string) => {
    // Implémentation téléchargement fichier
 // };

  const downloadFile = async (fileId: string, nomOriginal: string) => {
    try {
      const downloadUrl = `${API_URL}/api/documents/admin/download/${selectedDossier?.reference}/${selectedSousDossier?._id}/${fileId}`;

      const response = await fetch(downloadUrl, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        alert('Erreur lors du téléchargement');
        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = nomOriginal;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Erreur lors du téléchargement:', error);
      alert('Erreur lors du téléchargement du fichier');
    }
  };

  
    


//  const downloadTransfertFile = async (fileId: string, nomOriginal: string) => {
    // Implémentation téléchargement fichier transfert
  //};

const downloadTransfertFile = async (fileId: string, nomOriginal: string) => {
  try {
    if (!selectedTransfert) {
      alert('Aucun transfert sélectionné');
      return;
    }

    // ✅ Récupérer le document complet
    const response = await fetch(`${API_URL}/api/documents/admin/dossiers`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });

    const data = await response.json();
    const transfertDoc = data.dossiers.find((d: any) => d.reference === selectedTransfert.reference);

    if (!transfertDoc?.sousDossiers?.[0]?._id) {
      alert('Structure du document invalide');
      return;
    }

    const sousDossierId = transfertDoc.sousDossiers[0]._id;

    // ✅ Télécharger
    const downloadUrl = `${API_URL}/api/documents/admin/download/${selectedTransfert.reference}/${sousDossierId}/${fileId}`;

    const downloadResponse = await fetch(downloadUrl, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });

    if (!downloadResponse.ok) {
      alert('Erreur lors du téléchargement');
      return;
    }

    const blob = await downloadResponse.blob();
    const url = window.URL.createObjectURL(blob);
    const a = window.document.createElement('a'); // ✅ window.document
    a.href = url;
    a.download = nomOriginal;
    window.document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    window.document.body.removeChild(a);

  } catch (error) {
    console.error('Erreur téléchargement:', error);
    alert('Erreur lors du téléchargement du fichier');
  }
};






 // const deleteFile = async (fileId: string, fileName: string, chemin: string) => {
    // Implémentation suppression fichier
  //};
 



const deleteFile = async (fileId: string, fileName: string, chemin: string) => {
  if (!canDeleteSubfoldersAndFiles()) {
    alert('Vous n\'avez pas les permissions pour supprimer des fichiers');
    return;
  }

  const shouldDelete = window.confirm(`Supprimer le fichier "${fileName}" ?`);
  if (!shouldDelete) return;

  try {
    let endpoint = '';

    if (selectedDossier) {
      // Pour les documents voyage
      endpoint = `${API_URL}/api/documents/admin/file/${selectedDossier.reference}/${selectedSousDossier?._id}/${fileId}`;
    } else if (selectedTransfert) {
      // ✅ NOUVEAU : POUR LES TRANSFERTS
      const response = await fetch(`${API_URL}/api/documents/admin/dossiers`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      const transfertDoc = data.dossiers.find((d: any) => d.reference === selectedTransfert.reference);

      if (!transfertDoc?.sousDossiers?.[0]?._id) {
        alert('Structure du document invalide');
        return;
      }

      const sousDossierId = transfertDoc.sousDossiers[0]._id;
      endpoint = `${API_URL}/api/documents/admin/file/${selectedTransfert.reference}/${sousDossierId}/${fileId}`;
    }

    const response = await fetch(endpoint, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });

    if (response.ok) {
      alert('Fichier supprimé avec succès');
      setShowSousDossierModal(false);
      setShowDossierModal(false);
      setShowTransfertModal(false);
      await loadDossiers();
    }
  } catch (error) {
    console.error('Erreur lors de la suppression du fichier:', error);
  }
};




 

//  const handleDelete = async () => {
    // Implémentation suppression
  //};


const confirmDelete = (type: 'dossier' | 'sousdossier' | 'user' | 'file' | 'transfert', data: any) => {
  if (type === 'user' && currentUserRole !== 'admin_bank' && currentUserRole !== 'super_admin') {
    alert('Seuls les administrateurs peuvent supprimer des utilisateurs');
    return;
  }
  if ((type === 'dossier' || type === 'sousdossier' || type === 'file' || type === 'transfert') && !canDeleteSubfoldersAndFiles()) {
    alert('Vous n\'avez pas les permissions pour supprimer');
    return;
  }
  setDeleteTarget({ type, data });
  setShowDeleteModal(true);
};


  const handleDelete = async () => {
  if (!deleteTarget) return;

  try {
    let endpoint = '';
    if (deleteTarget.type === 'dossier') {
      endpoint = `${API_URL}/api/documents/admin/dossier/${deleteTarget.data.reference}`;
    } else if (deleteTarget.type === 'sousdossier') {
      endpoint = `${API_URL}/api/documents/admin/sousdossier/${selectedDossier?.reference}/${deleteTarget.data._id}`;
    } else if (deleteTarget.type === 'transfert') {
     // ✅ Les transferts sont dans /api/documents
     endpoint = `${API_URL}/api/documents/admin/dossier/${deleteTarget.data.reference}`;
  } else if (deleteTarget.type === 'user') {
      endpoint = `${API_URL}/api/users/${deleteTarget.data._id}`;
    }

    const response = await fetch(endpoint, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });

    if (response.ok) {
      if (deleteTarget.type === 'dossier') {
        await loadDossiers();
      } else if (deleteTarget.type === 'transfert') {
        await  loadDossiers();
      } else if (deleteTarget.type === 'user') {
        await loadUsers();
      } else {
        await loadDossiers();
      }
      alert('Suppression effectuée avec succès');
    }
  } catch (error) {
    console.error('Erreur lors de la suppression:', error);
  }

  setShowDeleteModal(false);
  setDeleteTarget(null);
  setShowDossierModal(false);
  setShowSousDossierModal(false);
  setShowTransfertModal(false);
  setShowUserModal(false);
};





  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('dossiers_statuts');
    window.location.href = '/admin/login';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-2 sm:p-4">
      <div className="max-w-full mx-auto px-1 sm:px-2">
        <DashboardHeader
          currentUser={currentUser}
          currentUserRole={currentUserRole}
          onLogout={handleLogout}
        />

        <StatsCards
          activeTab={activeTab}
          dossiers={dossiers}
          transferts={transferts}
          filteredUsers={filteredUsers}
          currentUserRole={currentUserRole}
        />

        <SearchFilters
          activeTab={activeTab}
          searchTerm={searchTerm}
          dateSearch={dateSearch}
          onSearchTermChange={setSearchTerm}
          onDateSearchChange={setDateSearch}
          onSearch={handleSearch}
          onReset={resetSearch}
        />

        <TabsNavigation
          activeTab={activeTab}
          onTabChange={setActiveTab}
          currentUserRole={currentUserRole}
          onExport={exportToExcel}
          onExportUsers={exportUsersToExcel}
          onShowFormulaire={() => setShowFormulaireModal(true)}
        />

        {activeTab === 'documents' && (
          <DocumentsTable
            dossiers={filteredDossiers}
            currentUserRole={currentUserRole}
            onOpenDossier={(dossier) => {
              setSelectedDossier(dossier);
              setShowDossierModal(true);
            }}
            onChangeStatut={changeStatut}
            onDelete={(dossier) => confirmDelete('dossier', dossier)}
          />
        )}

        {activeTab === 'transferts' && (
          <TransfertsTable
            transferts={filteredTransferts}
            currentUserRole={currentUserRole}
            onOpenTransfert={(transfert) => {
              setSelectedTransfert(transfert);
              setShowTransfertModal(true);
            }}
            onChangeStatut={changeTransfertStatut}
            onDelete={(transfert) => confirmDelete('transfert', transfert)}
          />
        )}

        {activeTab === 'users' && (
          <UsersTable
            users={filteredUsers}
            currentUserRole={currentUserRole}
            onToggleUserStatus={toggleUserStatus}
            onEditUser={(user) => {
              setSelectedUser(user);
              setUserForm({
                username: user.username,
                email: user.email,
                password: '',
                role: user.role
              });
              setShowUserModal(true);
            }}
            onDeleteUser={(user) => confirmDelete('user', user)}
            onCreateUser={() => setShowCreateUserModal(true)}
          />
        )}

        <DossierModal
          isOpen={showDossierModal}
          dossier={selectedDossier!}
          currentUserRole={currentUserRole}
          onClose={() => setShowDossierModal(false)}
          onOpenSousDossier={(sousDossier) => {
            setSelectedSousDossier(sousDossier);
            setShowSousDossierModal(true);
          }}
          onDeleteSousDossier={(sousDossier) => confirmDelete('sousdossier', sousDossier)}
          onDeleteDossier={(dossier) => confirmDelete('dossier', dossier)}
        />

        <SousDossierModal
          isOpen={showSousDossierModal}
          sousDossier={selectedSousDossier}
          dossier={selectedDossier}
          currentUserRole={currentUserRole}
          onClose={() => setShowSousDossierModal(false)}
          onDownloadFile={downloadFile}
          onDeleteFile={deleteFile}
          onDeleteSousDossier={(sousDossier) => confirmDelete('sousdossier', sousDossier)}
        />

        <TransfertModal
          isOpen={showTransfertModal}
          transfert={selectedTransfert!}
          currentUserRole={currentUserRole}
          onClose={() => setShowTransfertModal(false)}
          onDownloadFile={downloadTransfertFile}
          onDeleteFile={deleteFile}
          onDeleteTransfert={(transfert) => confirmDelete('transfert', transfert)}
        />

        <UserModal
          isOpen={showUserModal}
          user={selectedUser}
          userForm={userForm}
          currentUserRole={currentUserRole}
          onClose={() => setShowUserModal(false)}
          onUpdateUser={updateUser}
          onUserFormChange={setUserForm}
        />

        <CreateUserModal
          isOpen={showCreateUserModal}
          userForm={userForm}
          currentUserRole={currentUserRole}
          onClose={() => setShowCreateUserModal(false)}
          onCreateUser={createUser}
          onUserFormChange={setUserForm}
        />

        <RejectModal
          isOpen={showRejectModal}
          dossierToReject={dossierToReject}
          transfertToReject={transfertToReject}
          rejectReason={rejectReason}
          onClose={() => {
            setShowRejectModal(false);
            setRejectReason('');
            setDossierToReject(null);
            setTransfertToReject(null);
          }}
          onConfirmReject={confirmReject}
          onRejectReasonChange={setRejectReason}
        />

        <DeleteModal
          isOpen={showDeleteModal}
          deleteTarget={deleteTarget}
          onClose={() => setShowDeleteModal(false)}
          onConfirmDelete={handleDelete}
        />

        <FormulaireClientModal
          isOpen={showFormulaireModal}
          onClose={() => setShowFormulaireModal(false)}
          API_URL={API_URL}
        />
      </div>
    </div>
  );
};

export default AdminDashboard;
